import { useSyncExternalStore } from 'react';
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { isPaletteId, PALETTE_KEY, PALETTES, type PaletteId } from './palettes.ts';

/**
 * Tema: Ljust / Mörkt / System. Valet är lokalt för installationen (localStorage, nyckeln nedan) och
 * synkas aldrig med kontot. public/theme-boot.js sätter samma attribut innan något ritas; den här modulen
 * tar över efter starten, följer operativsystemets tema och sparar uttryckliga val.
 *
 * Befintliga installationer utan sparat val behåller det mörka utseendet; nya installationer börjar med
 * System. Otillgänglig lagring (privat läge, blockerad lagring) kraschar aldrig appen – valet gäller då
 * bara tills sidan stängs.
 *
 * Färgpaletten (Gryning / Norrsken / Original, palettes.ts) är ett separat val med egen nyckel. Befintliga
 * installationer utan sparad palett får Porslin (lagrings-ID original), nya börjar med Porslin.
 * Inget av valen påverkas av inloggning, utloggning, profilbyte eller synk.
 */
export type ThemePref = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_KEY = 'autoday.theme';
/** Färg för webbläsarens/systemets fält i Original. Samma värden som i public/theme-boot.js (mörkt = tidigare värde). */
export const THEME_COLOR: Record<ResolvedTheme, string> = { dark: PALETTES.original.dark.themeColor, light: PALETTES.original.light.themeColor };
export function themeColorFor(palette: PaletteId, resolved: ResolvedTheme): string {
  return PALETTES[palette][resolved].themeColor;
}
const LIGHT_QUERY = '(prefers-color-scheme: light)';

export function isThemePref(v: unknown): v is ThemePref {
  return v === 'light' || v === 'dark' || v === 'system';
}

/** System följer operativsystemet. Utan stöd för prefers-color-scheme blir det mörkt (appens grund). */
export function resolveTheme(pref: ThemePref, systemLight: boolean): ResolvedTheme {
  if (pref === 'system') return systemLight ? 'light' : 'dark';
  return pref;
}

export function readStoredPref(storage: () => Storage | null = defaultStorage): ThemePref | null {
  try {
    const v = storage()?.getItem(THEME_KEY);
    return isThemePref(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredPref(pref: ThemePref, storage: () => Storage | null = defaultStorage): boolean {
  try {
    const s = storage();
    if (!s) return false;
    s.setItem(THEME_KEY, pref);
    return s.getItem(THEME_KEY) === pref;
  } catch {
    return false;
  }
}

export function readStoredPalette(storage: () => Storage | null = defaultStorage): PaletteId | null {
  try {
    const v = storage()?.getItem(PALETTE_KEY);
    return isPaletteId(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredPalette(p: PaletteId, storage: () => Storage | null = defaultStorage): boolean {
  try {
    const s = storage();
    if (!s) return false;
    s.setItem(PALETTE_KEY, p);
    return s.getItem(PALETTE_KEY) === p;
  } catch {
    return false;
  }
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function systemPrefersLight(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia(LIGHT_QUERY).matches;
  } catch {
    return false;
  }
}

/** Sätter tema och palett på <html>, color-scheme och theme-color. Native: statusfältets ikoner (Capacitor SystemBars). */
export function applyTheme(resolved: ResolvedTheme, doc: Document = document, palette: PaletteId = 'original'): void {
  const root = doc.documentElement;
  root.dataset['theme'] = resolved;
  root.dataset['palette'] = palette;
  root.style.colorScheme = resolved;
  setMeta(doc, 'theme-color', themeColorFor(palette, resolved));
  setMeta(doc, 'color-scheme', resolved);
  void applyNativeBars(resolved);
}

function setMeta(doc: Document, name: string, content: string) {
  let m = doc.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!m) {
    m = doc.createElement('meta');
    m.name = name;
    doc.head.appendChild(m);
  }
  m.content = content;
}

async function applyNativeBars(resolved: ResolvedTheme) {
  try {
    if (!Capacitor.isNativePlatform()) return;
    // SystemBarsStyle.Dark = ljusa ikoner på mörk bakgrund, Light = mörka ikoner på ljus bakgrund.
    await SystemBars.setStyle({ style: resolved === 'dark' ? SystemBarsStyle.Dark : SystemBarsStyle.Light });
  } catch {
    // Äldre native-skal eller webb: inget statusfält att styra.
  }
}

interface ThemeSnapshot {
  pref: ThemePref;
  resolved: ResolvedTheme;
  /** false = valet kunde inte sparas (lagring otillgänglig) och gäller bara den här sessionen. */
  persisted: boolean;
  palette: PaletteId;
  palettePersisted: boolean;
}

class ThemeController {
  private snap: ThemeSnapshot;
  private listeners = new Set<() => void>();
  private started = false;

  constructor() {
    const pref = readStoredPref() ?? (typeof document !== 'undefined' && isThemePref(document.documentElement.dataset['themePref']) ? (document.documentElement.dataset['themePref'] as ThemePref) : 'dark');
    const bootPalette = typeof document !== 'undefined' ? document.documentElement.dataset['palette'] : undefined;
    const palette = readStoredPalette() ?? (isPaletteId(bootPalette) ? bootPalette : 'original');
    this.snap = { pref, resolved: resolveTheme(pref, systemPrefersLight()), persisted: readStoredPref() !== null, palette, palettePersisted: readStoredPalette() !== null };
  }

  get snapshot(): ThemeSnapshot {
    return this.snap;
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  /**
   * Körs en gång vid start. Saknas ett sparat val avgör `existingInstall` standardvalet: befintlig
   * installation → Mörkt (samma utseende som förut), ny installation → System. Valet sparas så att det
   * inte behöver avgöras igen.
   */
  async start(existingInstall: () => Promise<boolean>): Promise<void> {
    if (this.started) return;
    this.started = true;
    let pref = readStoredPref();
    let persisted = pref !== null;
    let palette = readStoredPalette();
    let palettePersisted = palette !== null;
    if (!pref || !palette) {
      let existing = true;
      try {
        existing = await existingInstall();
      } catch {
        existing = true; // vid osäkerhet: behåll det befintliga utseendet
      }
      if (!pref) {
        pref = existing ? 'dark' : 'system';
        persisted = writeStoredPref(pref);
      }
      if (!palette) {
        palette = 'original';
        palettePersisted = writeStoredPalette(palette);
      }
    }
    this.set(pref, persisted, palette, palettePersisted);
    delete document.documentElement.dataset['themePending'];
    try {
      matchMedia(LIGHT_QUERY).addEventListener('change', () => {
        if (this.snap.pref === 'system') this.set('system', this.snap.persisted, this.snap.palette, this.snap.palettePersisted);
      });
    } catch {
      // Ingen matchMedia: System blir mörkt.
    }
    // Ett val i en annan flik med samma webbadress.
    window.addEventListener('storage', (e) => {
      if (e.key === THEME_KEY && isThemePref(e.newValue)) this.set(e.newValue, true, this.snap.palette, this.snap.palettePersisted);
      if (e.key === PALETTE_KEY && isPaletteId(e.newValue)) this.set(this.snap.pref, this.snap.persisted, e.newValue, true);
    });
  }

  choose(pref: ThemePref): void {
    this.set(pref, writeStoredPref(pref), this.snap.palette, this.snap.palettePersisted);
  }

  choosePalette(palette: PaletteId): void {
    this.set(this.snap.pref, this.snap.persisted, palette, writeStoredPalette(palette));
  }

  private set(pref: ThemePref, persisted: boolean, palette: PaletteId, palettePersisted: boolean) {
    const resolved = resolveTheme(pref, systemPrefersLight());
    applyTheme(resolved, document, palette);
    const s = this.snap;
    if (pref === s.pref && resolved === s.resolved && persisted === s.persisted && palette === s.palette && palettePersisted === s.palettePersisted) return;
    this.snap = { pref, resolved, persisted, palette, palettePersisted };
    for (const l of this.listeners) l();
  }
}

let controller: ThemeController | null = null;
export function getTheme(): ThemeController {
  return (controller ??= new ThemeController());
}

export function useTheme(): ThemeSnapshot {
  const c = getTheme();
  return useSyncExternalStore(c.subscribe, () => c.snapshot, () => c.snapshot);
}
